import { cx } from "@uui/utils/cx"

/*
 * The house banner: a static statement that some part of the page is impaired
 * or needs attention. Not for actions or dismissal — that register belongs to
 * the kit's alerts.
 *
 *   caution    caution.50 ground, a SOLID 3px caution rule — needs a person
 *   remote     dashed print edge — live, fetched on view, provisional
 *   confirmed  hairline card with a do dot — something landed
 *   system     quiet hairline card — informational
 *
 * Legacy tones are accepted: info → system · warning, error → caution.
 */
type Tone =
  | "caution"
  | "remote"
  | "confirmed"
  | "system"
  | "info"
  | "warning"
  | "error"
  | "ink"

const resolve = (tone: Tone) =>
  tone === "warning" || tone === "error"
    ? "caution"
    : tone === "info" || tone === "ink"
      ? "system"
      : tone

export function Notice({
  tone,
  role,
  children,
  className,
}: {
  tone: Tone
  /** "alert" interrupts, "status" is polite. Omit for purely visual notices. */
  role?: "alert" | "status"
  children: React.ReactNode
  className?: string
}) {
  const t = resolve(tone)
  return (
    <div
      role={role}
      className={cx(
        "px-3.5 py-2.5 text-sm",
        t === "caution" && "caution-band text-caution-700",
        t === "remote" && "remote text-secondary flex items-start gap-2",
        t === "confirmed" && "card text-secondary flex items-start gap-2",
        t === "system" && "card text-tertiary",
        className,
      )}
    >
      {t === "remote" && (
        <span aria-hidden="true" className="remote-dot mt-[6px]" />
      )}
      {t === "confirmed" && (
        <span aria-hidden="true" className="confirmed-dot mt-[6px]" />
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}
